import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  CookieConsentBanner,
  PlatformAppShell,
  showErrorToast,
  useActivityHeartbeat,
  useEditorWorkspaceStore,
  useSiteSupportPopups,
  type HeaderUserMenuItem,
  type NavLeaf
} from '@jarvis/platform-ui';
import { signoutUrl } from '../../api/auth';
import { APP_REGISTRY_BY_ID } from '../../apps/appRegistry';
import { MyAppsGlobalExperience } from '../../apps/global/MyAppsGlobalExperience';
import { MyAppsRecentMenu } from '../../apps/global/MyAppsRecentMenu';
import { launchMyAppsEntry, openExternalAppUrl } from '../../apps/myAppsLaunch';
import { buildMyAppsSidebarLeaves } from '../../apps/myAppsNav';
import { getPermittedSearchEntries, type MyAppsSearchResult } from '../../apps/myAppsSearch';
import { resolveRecentToolId } from '../../apps/resolveRecentToolId';
import { useMyAppsStore } from '../../apps/useMyAppsStore';
import { MEDIA_LIBRARY_APP_ID, type AssistantRole } from '../../auth/roles';
import { useAuthStore } from '../../auth/useAuthStore';
import { useHasRole } from '../../auth/useHasRole';
import {
  COOKIE_POLICY_VERSION,
  SITE_SUPPORT_COPY,
  siteFeedbackApi
} from '../../siteSupport/config';
import { useSiteCapabilitiesStore } from '../../site/useSiteCapabilitiesStore';
import { useShellPreferencesStore } from '../../shell/useShellPreferencesStore';
import { useThemeStore } from '../../theme/useThemeStore';
import './AppShell.css';

interface AssistantNavLeaf extends NavLeaf {
  /** Entra App Role required to show this item (optional). SysAdmin for Site Admin; elevated triad for IT apps. */
  role?: AssistantRole;
  items?: AssistantNavLeaf[];
}

function filterNav(
  items: AssistantNavLeaf[],
  hasRole: (role: AssistantRole) => boolean
): AssistantNavLeaf[] {
  const out: AssistantNavLeaf[] = [];
  for (const node of items) {
    const kids = node.items ? filterNav(node.items, hasRole) : undefined;
    const selfAllowed = !node.role || hasRole(node.role);
    const leaf = node.path !== undefined;
    if (leaf) {
      if (selfAllowed) out.push({ ...node, items: kids });
      continue;
    }
    if (selfAllowed && kids && kids.length > 0) {
      out.push({ ...node, items: kids });
    }
  }
  return out;
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const username = useAuthStore((s) => s.username);
  const roles = useAuthStore((s) => s.roles);
  const authStatus = useAuthStore((s) => s.status);
  const mode = useThemeStore((s) => s.mode);
  const toggleMode = useThemeStore((s) => s.toggleMode);
  const showThemeToggle = useThemeStore((s) => s.canToggleMode());
  const { hasAssistantRole, canAccessMediaLibrary } = useHasRole();
  const analyticsEnabled = useSiteCapabilitiesStore((s) => s.analyticsEnabled);
  const enabledAppIds = useMyAppsStore((s) => s.enabledAppIds);
  const pinnedToolIds = useMyAppsStore((s) => s.pinnedToolIds);
  const recentToolIds = useMyAppsStore((s) => s.recentToolIds);
  const externalApps = useMyAppsStore((s) => s.externalApps);
  const myAppsStatus = useMyAppsStore((s) => s.status);
  const loadMyApps = useMyAppsStore((s) => s.load);
  const resetMyApps = useMyAppsStore((s) => s.reset);
  const togglePin = useMyAppsStore((s) => s.togglePin);
  const recordRecent = useMyAppsStore((s) => s.recordRecent);
  const clearRecent = useMyAppsStore((s) => s.clearRecent);
  const appEntries = useMemo(
    () => getPermittedSearchEntries(roles, externalApps, analyticsEnabled),
    [analyticsEnabled, externalApps, roles]
  );
  const appEntriesById = useMemo(
    () => new Map(appEntries.map((entry) => [entry.id, entry])),
    [appEntries]
  );
  const recentEntries = useMemo(
    () =>
      recentToolIds.flatMap((appId) => {
        const entry = appEntriesById.get(appId);
        return entry ? [entry] : [];
      }),
    [appEntriesById, recentToolIds]
  );
  const hasAvailableMyApps =
    myAppsStatus === 'ready' &&
    enabledAppIds.some((appId) => {
      const app = APP_REGISTRY_BY_ID.get(appId);
      if (!app) return false;
      if (app.id === MEDIA_LIBRARY_APP_ID) return canAccessMediaLibrary();
      return hasAssistantRole(app.minRole);
    });

  useEffect(() => {
    if (authStatus === 'authenticated') {
      void loadMyApps();
    } else {
      resetMyApps();
    }
  }, [authStatus, loadMyApps, resetMyApps]);

  useEffect(() => {
    if (authStatus !== 'authenticated' || myAppsStatus !== 'ready') return;
    const recentId = resolveRecentToolId(location.pathname, new Set(appEntriesById.keys()));
    if (recentId) {
      void recordRecent(recentId).catch(() => undefined);
    }
  }, [appEntriesById, authStatus, location.pathname, myAppsStatus, recordRecent]);

  const openWorkspaceTabCount = useEditorWorkspaceStore((s) => s.tabs.length);

  const navItems = useMemo(() => {
    const workspaceLabel =
      openWorkspaceTabCount > 0 ? `Workspace (${openWorkspaceTabCount})` : 'Workspace';
    const pinnedIds = myAppsStatus === 'ready' ? pinnedToolIds : [];
    const customAppIds =
      myAppsStatus === 'ready'
        ? appEntries.filter((entry) => entry.kind === 'link').map((entry) => entry.id)
        : [];

    const items: AssistantNavLeaf[] = [
      { id: 'dashboard', text: 'Dashboard', path: '/' },
      {
        id: 'files',
        text: 'Files',
        items: [
          { id: 'files-browser', text: 'File Browser', path: '/files' },
          { id: 'files-workspace', text: workspaceLabel, path: '/files/workspace' }
        ]
      },
      {
        id: 'my-apps',
        text: 'My Apps',
        items: buildMyAppsSidebarLeaves(pinnedIds, appEntriesById, customAppIds).map((leaf) => {
          const url = leaf.url;
          return url
            ? {
                id: leaf.id,
                text: leaf.text,
                path: leaf.path,
                onSelect: () => {
                  if (openExternalAppUrl(url)) {
                    void recordRecent(leaf.id).catch(() => undefined);
                    return;
                  }
                  showErrorToast('Could not open this app.');
                }
              }
            : { id: leaf.id, text: leaf.text, path: leaf.path };
        })
      }
    ];

    items.push({
      id: 'site-admin',
      text: 'Site Administration',
      role: 'AssistantSysAdmin',
      items: [
        {
          id: 'storage',
          text: 'Storage',
          role: 'AssistantSysAdmin',
          items: [
            {
              id: 'storage-mounts',
              text: 'Storage Mounts',
              path: '/admin/storage-mounts',
              role: 'AssistantSysAdmin'
            },
            {
              id: 'media-library-config',
              text: 'Media Library Config',
              path: '/admin/media-library-config',
              role: 'AssistantSysAdmin'
            },
            {
              id: 'ascii-art-library',
              text: 'ASCII Art Library',
              path: '/admin/ascii-art',
              role: 'AssistantSysAdmin'
            },
            {
              id: 'emoji-library',
              text: 'Emoji Library',
              path: '/admin/emoji-library',
              role: 'AssistantSysAdmin'
            },
            {
              id: 'gif-library',
              text: 'GIF Library',
              path: '/admin/gif-library',
              role: 'AssistantSysAdmin'
            },
            {
              id: 'storage-file-audit',
              text: 'File Operation Audit',
              path: '/admin/storage-file-audit',
              role: 'AssistantSysAdmin'
            }
          ]
        },
        {
          id: 'user-activity',
          text: 'User Activity',
          path: '/admin/user-activity',
          role: 'AssistantSysAdmin'
        },
        { id: 'admin-bugs', text: 'Bug Reports', path: '/admin/bugs', role: 'AssistantSysAdmin' },
        {
          id: 'admin-feedback',
          text: 'User Feedback',
          path: '/admin/feedback',
          role: 'AssistantSysAdmin'
        },
        {
          id: 'admin-support-metadata',
          text: 'Support Metadata',
          path: '/admin/support-metadata',
          role: 'AssistantSysAdmin'
        },
        {
          id: 'admin-custom-apps',
          text: 'Custom Apps',
          path: '/admin/custom-apps',
          role: 'AssistantSysAdmin'
        },
        ...(analyticsEnabled
          ? [
              {
                id: 'admin-openmetadata',
                text: 'OpenMetadata',
                path: '/admin/openmetadata',
                role: 'AssistantSysAdmin' as const
              }
            ]
          : [])
      ]
    });

    return filterNav(items, hasAssistantRole);
  }, [
    analyticsEnabled,
    appEntries,
    appEntriesById,
    hasAssistantRole,
    myAppsStatus,
    openWorkspaceTabCount,
    pinnedToolIds,
    recordRecent
  ]);

  useActivityHeartbeat({
    enabled: authStatus === 'authenticated',
    themeStorageKey: 'assistantui:theme'
  });
  const { openBugReport, openFeedback, popups } = useSiteSupportPopups(
    siteFeedbackApi,
    SITE_SUPPORT_COPY
  );

  const userMenuItems = useMemo((): HeaderUserMenuItem[] => {
    return [
      { id: 'profile', text: 'User Profile' },
      { id: 'settings', text: 'User Settings' },
      { id: 'my-tickets', text: 'My Tickets', beginGroup: true },
      { id: 'feedback', text: 'Feedback' },
      { id: 'report-bug', text: 'Bug Report' },
      { id: 'logout', text: 'Sign out', beginGroup: true }
    ];
  }, []);

  function onUserMenuClick(id: string) {
    if (id === 'logout') window.location.assign(signoutUrl());
    if (id === 'profile') navigate('/settings/profile');
    if (id === 'settings') navigate('/settings/general');
    if (id === 'my-tickets') navigate('/my-tickets');
    if (id === 'feedback') openFeedback();
    if (id === 'report-bug') openBugReport();
  }

  function onMyAppsSelect(result: MyAppsSearchResult) {
    if (!launchMyAppsEntry(result, navigate)) {
      showErrorToast('Could not open this app.');
      return;
    }
    if (result.kind === 'link') {
      void recordRecent(result.id).catch(() => undefined);
    }
  }

  function onTogglePin(toolId: string, pinned: boolean) {
    void togglePin(toolId, pinned).catch((err) =>
      showErrorToast(err instanceof Error ? err.message : 'Could not update pinned tools.')
    );
  }

  function onClearRecent() {
    void clearRecent().catch((err) =>
      showErrorToast(err instanceof Error ? err.message : 'Could not clear recent tools.')
    );
  }

  return (
    <>
      <PlatformAppShell
        storageKeyPrefix="assistantui"
        navItems={navItems}
        useShellPreferencesStore={useShellPreferencesStore}
        header={{
          brand: {
            logoSrc: '/img/assistant-logo.webp',
            title: 'JARVIS IT Assistant',
            subtitle: 'Personalized digital IT assistant always there to help.'
          },
          username,
          themeMode: mode,
          onThemeToggle: toggleMode,
          showThemeToggle,
          toolbarLeadingActions: hasAvailableMyApps ? (
            <MyAppsRecentMenu
              recent={recentEntries}
              onSelect={onMyAppsSelect}
              onClearRecent={onClearRecent}
            />
          ) : null,
          userMenuItems,
          onUserMenuClick
        }}
      />
      {popups}
      <MyAppsGlobalExperience
        roles={roles}
        pinnedToolIds={pinnedToolIds}
        recentToolIds={recentToolIds}
        externalApps={externalApps}
        onSelect={onMyAppsSelect}
        onTogglePin={onTogglePin}
        onClearRecent={onClearRecent}
      />
      <CookieConsentBanner
        storageKey="assistantui:cookie-consent"
        policyVersion={COOKIE_POLICY_VERSION}
        privacyPath="/settings/privacy"
        cookiesPath="/settings/cookies"
        termsPath="/settings/terms"
        api={siteFeedbackApi}
      />
    </>
  );
}
